import { Users, Heart, Mountain, GraduationCap, Home } from 'lucide-react';

export const UseCaseShowcase = () => {
  const useCases = [
    {
      icon: Users,
      badge: "Youth Summer Camps",
      headline: "Feeding Hundreds, Session After Session",
      description: "Feeding hundreds of campers over multiple sessions takes precision and planning. CampChef Pro automates menus and Sysco orders so every meal is ready on time — without the chaos of spreadsheets.",
      metric: "40+ hours",
      metricLabel: "saved per week in planning time",
      testimonial: "CampChef Pro helped us finally stay ahead of our kitchen schedule. We can focus on campers, not spreadsheets.",
      author: "Sarah Johnson",
      role: "Director, Pine Ridge Summer Camp",
      image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200&q=80",
      imagePosition: "left"
    },
    {
      icon: Heart,
      badge: "Faith-Based Retreat Centers",
      headline: "Empowering Volunteers, Simplifying Service",
      description: "Weekend retreats move fast — new groups, new menus, limited staff. CampChef Pro keeps every ingredient organized and orders accurate so your team can focus on service, not spreadsheets.",
      metric: "99.7%",
      metricLabel: "order accuracy",
      testimonial: "Our volunteers love it. It's easy, clean, and saves us so much time.",
      author: "Elena Martinez",
      role: "Retreat Chef, Horizon Faith Center",
      image: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=1200&q=80",
      imagePosition: "right"
    },
    {
      icon: Mountain,
      badge: "Outdoor Adventure & Leadership Programs",
      headline: "Built for the Trail and the Kitchen",
      description: "Adventure programs need flexibility. Whether you're cooking in a lodge or on a trailhead, CampChef Pro calculates packable quantities and automates inventory tracking for mobile kitchens.",
      metric: "20%",
      metricLabel: "waste reduction",
      testimonial: "Finally, a kitchen system that works even when we're off-grid.",
      author: "Jake Rivera",
      role: "Program Director, Sierra Leadership Camp",
      image: "https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?w=1200&q=80",
      imagePosition: "left"
    },
    {
      icon: GraduationCap,
      badge: "School & Nonprofit Camps",
      headline: "Every Ingredient Accounted For, Every Dollar Stretched",
      description: "School and nonprofit camps operate on tight budgets. CampChef Pro keeps every ingredient accounted for — making it easy to plan nutritious meals and stretch every dollar.",
      metric: "+15%",
      metricLabel: "food cost efficiency",
      testimonial: "We can track every item now. Our reports are clean and clear.",
      author: "Maria Torres",
      role: "Camp Director, Camp Horizon",
      image: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1200&q=80",
      imagePosition: "right"
    },
    {
      icon: Home,
      badge: "Family & Multi-Generational Camps",
      headline: "Feeding Families, From Toddlers to Grandparents",
      description: "Multi-generational retreats bring variety to the menu — CampChef Pro makes it easy to manage allergies, age-based portions, and rotating meal styles all in one plan.",
      metric: "100%",
      metricLabel: "satisfaction across all ages",
      testimonial: "Everyone eats well — no confusion, no double-orders.",
      author: "Daniel Chen",
      role: "Kitchen Manager, The Summit California",
      image: "https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?w=1200&q=80",
      imagePosition: "left"
    }
  ];

  return (
    <div>
      {useCases.map((useCase, index) => {
        const Icon = useCase.icon;
        const isEven = index % 2 === 1;
        const bgColor = isEven ? 'bg-[#F5F1E8]' : 'bg-white';
        
        return (
          <section key={index} className={`py-16 ${bgColor}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                {/* Image side */}
                <div 
                  className={`relative h-96 rounded-2xl overflow-hidden shadow-2xl ${
                    useCase.imagePosition === 'left' ? 'md:order-1' : 'md:order-2'
                  }`}
                >
                  <img 
                    src={useCase.image} 
                    alt={useCase.badge}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-deep-teal/40 to-transparent"></div>
                </div>
                
                {/* Content side */}
                <div className={useCase.imagePosition === 'left' ? 'md:order-2' : 'md:order-1'}>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-deep-teal/10 rounded-full mb-4">
                    <Icon className="h-5 w-5 text-deep-teal" />
                    <span className="text-sm font-semibold text-deep-teal">{useCase.badge}</span>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-slate mb-4">
                    {useCase.headline}
                  </h3>
                  
                  <p className="text-lg text-slate/80 leading-relaxed mb-6">
                    {useCase.description}
                  </p>
                  
                  {/* Metric Highlight */}
                  <div className="bg-copper/10 border-l-4 border-copper p-4 rounded-r-lg mb-6">
                    <div className="text-2xl font-bold text-copper mb-1">{useCase.metric}</div>
                    <div className="text-sm text-slate/70">{useCase.metricLabel}</div>
                  </div>
                  
                  {/* Testimonial */}
                  <blockquote className="border-l-4 border-deep-teal/30 pl-4 italic text-slate/80 mb-2">
                    "{useCase.testimonial}"
                  </blockquote>
                  <div className="pl-4 text-sm text-deep-teal font-medium">
                    — {useCase.author}, {useCase.role}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};
